import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { toast } from "react-toastify";
import { Box } from "@mui/material";
import Modal from "@mui/material/Modal";
import logo from "../image/logo.png";
import "react-toastify/dist/ReactToastify.css";

import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import "./Login.css";
import { BASE_URL } from "../../global";
import axiosWithToken from "../../utils/commonComponent/axiosTokenConfig";
import { fetchRBACData, getEmployee, getEmployeeData } from "../rbac/hasAccess";
import { ROLES, useGetRoleInfo } from "../../utils/commonComponent/common";

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    border: "2px solid cadetblue",
    padding: "20px",
    textAlign: "center",
    borderRadius: "25px",
    display: "flex",
    flexDirection: "column",
    width: "90%",
    maxWidth: "320px",
    height: "auto",
    minHeight: 200,
  };

function Login(props) {
  const navigate = useNavigate();

  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);
  const { setCurrentRole, setRoles } = useGetRoleInfo();

  // Load saved credentials on component mount
  useEffect(() => {
    const savedUserName = localStorage.getItem("rememberUserName");
    const savedPassword = localStorage.getItem("rememberPassword");
    const savedRememberMe = localStorage.getItem("rememberMe") === "true";

    if (savedRememberMe && savedUserName && savedPassword) {
      setUserName(savedUserName);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const configure = async (
    _id,
    _userName,
    type,
    modules = "",
    employeeId = 0,
    roles,
    editAccess,
    disciplineId,
    groupId,
    clientId,
  ) => {
    try {
      // Store user data in storage
      sessionStorage.setItem("isLoggedIn", true);
      localStorage.setItem("id", _id);
      localStorage.setItem("employeeId", employeeId);
      localStorage.setItem("userName", _userName);
      localStorage.setItem("type", type);
      localStorage.setItem("modules", modules);
      localStorage.setItem("editAccess", editAccess);
      localStorage.setItem("disciplineId", disciplineId);
      localStorage.setItem("groupId", groupId);
            localStorage.setItem("clientId", clientId);

      // Map roles to objects from ROLES
      let tRoles = (roles?.split(",") || [])
        .map((id) => ROLES[id])
        .filter(Boolean);

      // Fetch active role settings
      const { data: roleSettings = [] } = await axiosWithToken.get(
        `${BASE_URL}settings?_where=(keyName,like,activeRole~)~and(status,ne,-1)`,
      );

      // Build lookup from roleSettings
      const settingMap = Object.fromEntries(
        roleSettings.map(({ keyName, value }) => {
          const id = keyName.replace("activeRole", "");
          return [id, { name: value, id }];
        }),
      );

      // Merge roles with settings and filter
      tRoles = tRoles
        ?.map((role) => ({ ...role, ...settingMap[role.id] }))
        ?.filter((role) => role.name?.toLowerCase() !== "false");

      // Set current and all roles
      const tCurrentRole = tRoles[0] || {};
      setCurrentRole(tCurrentRole);
      setRoles(tRoles);

      return true;
    } catch (err) {
      console.error("Role/settings fetch error:", err);
    }
  };

const renderModal = () => {
  return (
    <Modal open={modalOpen} onClose={handleClose}>
      <Box sx={modalStyle}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: "18px" }}>
            Kindly contact your admin to reset your credentials!
          </span>
        </div>
      </Box>
    </Modal>
  );
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const res = await axiosWithToken.post(BASE_URL + "login", {
        userName: userName,
        password: password,
      });

      const userData = res.data?.user;

      if (userData?.userStatus !== 1) {
        toast.error("User is not Active");
        setTimeout(() => {
          window.location.reload();
        }, 2500);
        return;
      }

      if (res.status === 200 && userData) {
        const authToken = res.data.token;
        sessionStorage.setItem("authToken", authToken);
        sessionStorage.setItem("logInUser", userData?.userName);

        // Handle Remember Me functionality
        if (rememberMe) {
          localStorage.setItem("rememberUserName", userName);
          localStorage.setItem("rememberPassword", password);
          localStorage.setItem("rememberMe", "true");
        }

        // Wait for all API calls to complete
        try {
          // Configure user roles and permissions
          await configure(
            userData?.id,
            userData?.userName,
            userData?.type,
            userData?.moduleAccess,
            userData?.employeeId,
            userData?.roles,
            userData?.editAccess,
            userData?.disciplineId,
            userData?.groupId,
            userData?.clientId,
          );
          // Fetch and store employee designation
          await getEmployeeData(userData?.employeeId);
          // Fetch and store employee name
          await getEmployee(userData?.employeeId);

          // Fetch and store RBAC data
          await fetchRBACData(true, null);

          toast.success("Login Successful!");
          setIsLoading(false);
          // Navigate only after all API calls are complete
          navigate("/dashboard", { replace: true });
          window.location.reload(true);
        } catch (configError) {
          console.error("error:", configError);
          setIsLoading(false);
        }
      } else {
        setError("Incorrect Username / password");
        setUserName("");
        setPassword("");
        setIsLoading(false);
      }
      // Handle login attempt counting
      let falseLoginCount = Number(localStorage.getItem("loginCount") || 0);

      if (falseLoginCount > 2) {
        localStorage.setItem("blockTimer", Date.now() + 60000);
      }

      localStorage.setItem("loginCount", falseLoginCount + 1);
    } catch (error) {
      console.error("Login error:", error);
      setError(error.response?.data?.error || "An error occurred");
      // setIsLoading(false);
      setTimeout(() => {
        window.location.reload();
      }, 700);

      // Handle login attempt counting on error
      let falseLoginCount = Number(localStorage.getItem("loginCount") || 0);
      if (falseLoginCount > 2) {
        localStorage.setItem("blockTimer", Date.now() + 60000);
      }
      localStorage.setItem("loginCount", falseLoginCount + 1);

      toast.error("Incorrect Username / password");
    }
  };

  return (
    <>
      <div className="limiter">
        <div className="container-login100">
          <div className="wrap-login100">
            <div className="login100-pic js-tilt" data-tilt>
              <img className="" src={logo} alt="IMG" />
              <p className="login-lims-text">Laboratory Information<br/>Management System</p>
            </div>

            <div className="login100-form">
              <span className="login100-form-title">Welcome To LABAXIS</span>
              <p className="login-subtitle">Please sign in to continue</p>  

                <div className="wrap-input100 validate-input input-group-focus" data-validate="Valid email is required: ex@abc.xyz" style={{ position: "relative" }}>
                    <label className="login-label">Username</label>
                    <span className="symbol-input100">
                    <PersonOutlineIcon style={{ fontSize: "20px" }} />
                  </span>
                  <input
                    className="input100"
                    type="text"
                    name="email"
                    placeholder="Enter your username"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                />
                <span className="focus-input100"></span>
              </div>

              <div className="wrap-input100 validate-input input-group-focus" data-validate="Password is required" style={{ position: "relative" }}>
                  <label className="login-label">Password</label>
                  <span className="symbol-input100">
                    <LockOutlinedIcon style={{ fontSize: "20px" }} />
                  </span>
                  <input
                    className="input100"
                    type={showPassword ? "text" : "password"}
                    name="pass"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                <span
                  className="symbol-input100-right"
                  onClick={() => setShowPassword(prev => !prev)}
                  style={{ cursor: "pointer" }}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </span>
              </div>
              <span style={{ color: "red" }}>{error}</span>

              {/* Remember Me Checkbox */}
              <div className="text-left p-t-12" style={{ margin: "10px 20px" }}>
                <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    style={{ marginRight: "8px", cursor: "pointer" }}
                  />
                  <span className="txt1">Remember me</span>
                </label>
              </div>

              <div className="container-login100-form-btn">
                <button
                  className="login100-form-btn"
                  onClick={(e) => {
                    setIsLoading(true);
                    handleSubmit(e);
                  }}
                  style={{
                    opacity: isLoading ? 0.5 : 1,
                    backgroundColor: isLoading ? "#ccc" : "#57b846",
                  }}
                >
                  Login
                </button>
              </div>

              <div className="text-center p-t-12">
                <span className="txt1">Forgot </span>
                <button
                  className="txt2"
                  style={{ backgroundColor: "#0000" }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpen(true);
                  }}
                >
                  Username / Password?
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {renderModal()}
    </>
  );
}

export default Login;
